import React, { Suspense, lazy } from 'react';
import { RootState } from 'typesafe-actions';
import { Remote } from '@okapi/remote';
import { getRemoteTimeout, getRouterHash, getEnv } from 'common/selectors';
import { connect } from 'react-redux';
import { Redirect } from '@okapi/router-react';
import { checkIfThereIs404Error, checkIfThereIs500Error } from 'features/errors-hook';
import { ReviewsFormContent } from './reviews-form-content';
import { Styles } from './styles';
import { PageMeta } from './page-meta';
import { PdpContent, PdpContentProps } from './pdp-content-wrapped';
import { BasketLogic } from '../../features/basket';
import { StretchBanner } from '../../features/stretch-banner';

const Footer = lazy(() => import('./footer'));

export type PDPConnectedProps = {
  routerHash: string;
  thereIs404Error: boolean;
  thereIs500Error: boolean;
  remoteTimeout: number;
  disableAppsflyer: boolean;
};

export const PDP: React.FC<PDPConnectedProps> = ({
  routerHash,
  thereIs404Error,
  thereIs500Error,
  remoteTimeout,
  disableAppsflyer,
}) => {
  const { Content, wrapperClassname } = React.useMemo(
    () => resolveContent(routerHash, thereIs404Error, thereIs500Error),
    [routerHash, thereIs404Error, thereIs500Error],
  );

  return (
    <>
      <PageMeta isDev={process.env.NODE_ENV === 'development'} />
      <div className={wrapperClassname}>
        <StretchBanner />
        <Remote
          widget="header-footer/header"
          id="pdp-header"
          data={{ disableSticky: true, disableAppsflyer }}
          serverTimeout={remoteTimeout}
        />
        <BasketLogic serverTimeout={remoteTimeout} />
        <Content remoteTimeout={remoteTimeout} />
        <Suspense fallback="...Загрузка">
          <Footer remoteTimeout={remoteTimeout} />
        </Suspense>
      </div>
    </>
  );
};

export const resolveContent = (routerHash: string, thereIs404Error: boolean, thereIs500Error: boolean) => {
  let Content: React.ComponentType<PdpContentProps> = PdpContent;
  let wrapperClassname: string | undefined;

  if (thereIs500Error) {
    Content = Page500;
  } else if (thereIs404Error) {
    Content = Page404;
  } else if (routerHash === '#reviews-form') {
    Content = ReviewsFormContent;
    wrapperClassname = Styles.pdpReviewsFormContentWrapper;
  }
  return { Content, wrapperClassname };
};

export const Page500 = () => <Redirect to="/500" />;
export const Page404 = () => <Redirect to="/404" />;

export const mapStateToProps = (state: RootState): PDPConnectedProps => {
  const { USE_APPSFLYER = true } = getEnv(state);
  const routerHash = getRouterHash(state);
  const thereIs404Error = checkIfThereIs404Error(state);
  const thereIs500Error = checkIfThereIs500Error(state);
  const remoteTimeout = getRemoteTimeout(state);

  return {
    routerHash,
    thereIs404Error,
    thereIs500Error,
    remoteTimeout,
    disableAppsflyer: !USE_APPSFLYER,
  };
};

export const PDPConnected = connect(mapStateToProps)(PDP);
